import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Save, ArrowUp, ArrowDown, Link2, Loader2, AlertCircle, Users } from 'lucide-react';
import { api } from '../lib/auth';
import { invalidateDepos } from '../lib/useDepos';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

/**
 * Depo Listesi (Çalışma Durumu V2 / Çalışan Profilinde kullanılır)
 * --------------------------------------------------------------
 * Bu sayfa SUPER_ADMIN için: `depos` koleksiyonundaki listeyi yönetir
 * (ekle/düzenle/sil/sırala). Her depoyu opsiyonel olarak Planning'deki
 * karşılığına bağlar (`planning_depot_id` soft-link).
 *
 * NOT: Buradaki "depo" çalışanın fiziksel lokasyonu (`user.depo`) için
 * kullanılır — Planning sayfasındaki Depo & Rota modal ile FARKLI bir
 * koleksiyondur. Soft-link sadece UI tarafında "Bu depo Planning'de
 * Oevel ile aynı yere işaret eder" demek için.
 */
export default function DepotListSettings() {
  const [depos, setDepos] = useState([]);
  const [planningDepots, setPlanningDepots] = useState([]);
  const [companies, setCompanies] = useState({}); // company_id → {name}
  const [usage, setUsage] = useState({}); // depo.value → [{id, full_name}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [pendingMap, setPendingMap] = useState({});  // depo.id → planning_depot_id (string|null)
  const [pendingLabel, setPendingLabel] = useState({}); // depo.id → label
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [a, b, c, u] = await Promise.all([
        api.get('/depos'),
        api.get('/planning/depots'),
        api.get('/planning/companies').catch(() => ({ data: { data: [] } })),
        api.get('/depos/usage').catch(() => ({ data: { data: {} } })),
      ]);
      setDepos(a.data?.data || []);
      setPlanningDepots(b.data?.data || []);
      const compMap = {};
      for (const c2 of (c.data?.data || [])) compMap[c2.id] = c2;
      setCompanies(compMap);
      setUsage(u.data?.data || {});
    } catch (err) {
      toast.error('Depo listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const planningOptions = useMemo(() => {
    return planningDepots.map((pd) => ({
      id: pd.id,
      name: pd.name,
      company: companies[pd.company_id]?.name || '—',
    }));
  }, [planningDepots, companies]);

  const updateDepo = async (depo, patch) => {
    setSaving((s) => ({ ...s, [depo.id]: true }));
    try {
      await api.put(`/depos/${depo.id}`, patch);
      toast.success('Güncellendi');
      // Optimistic local update
      setDepos((list) => list.map((d) => d.id === depo.id ? { ...d, ...patch } : d));
      // Clear pending entries for this depo
      setPendingMap((p) => { const n = { ...p }; delete n[depo.id]; return n; });
      setPendingLabel((p) => { const n = { ...p }; delete n[depo.id]; return n; });
      // Cross-page cache: Users / DailyStatus / WorkStats dropdown'ları
      // yeni etiketi anında görsün diye global cache'i geçersiz kıl.
      invalidateDepos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving((s) => ({ ...s, [depo.id]: false }));
    }
  };

  const moveOrder = async (depo, dir) => {
    const sorted = [...depos].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex((d) => d.id === depo.id);
    if (idx < 0) return;
    const target = sorted[idx + dir];
    if (!target) return;
    // Swap sort_order values
    const a = depo.sort_order;
    const b = target.sort_order;
    setSaving((s) => ({ ...s, [depo.id]: true, [target.id]: true }));
    try {
      await Promise.all([
        api.put(`/depos/${depo.id}`, { sort_order: b }),
        api.put(`/depos/${target.id}`, { sort_order: a }),
      ]);
      setDepos((list) => list.map((d) => {
        if (d.id === depo.id) return { ...d, sort_order: b };
        if (d.id === target.id) return { ...d, sort_order: a };
        return d;
      }));
      invalidateDepos();
    } finally {
      setSaving((s) => ({ ...s, [depo.id]: false, [target.id]: false }));
    }
  };

  const handleCreate = async () => {
    const v = newValue.trim();
    const l = newLabel.trim();
    if (!v || !l) {
      toast.error('Hem slug hem etiket gerekli');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/depos', { value: v, label: l });
      if (res.data?.success === false) {
        toast.error(res.data?.detail || 'Eklenemedi');
        return;
      }
      toast.success('Depo eklendi');
      setNewValue('');
      setNewLabel('');
      invalidateDepos();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eklenemedi');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (depo) => {
    const affected = (usage[depo.value] || []).length;
    const warn = affected > 0
      ? `\n\n⚠️ ${affected} çalışanın depo alanı "${depo.value}" olarak ayarlı — silme sonrası bu çalışanların depo alanı boş gibi davranır.`
      : `\n\nBu çalışanlardan herhangi birinin profilinde "${depo.value}" değeri varsa, depo alanı boş gibi davranır.`;
    if (!window.confirm(`"${depo.label}" deposunu silmek istediğinize emin misiniz?${warn}`)) return;
    setDeletingId(depo.id);
    try {
      await api.delete(`/depos/${depo.id}`);
      toast.success('Silindi');
      invalidateDepos();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Depo Listesi</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Çalışan profilinde ve Çalışma Durumu V2 filtresinde kullanılan depo etiketleri. Her depoyu
          opsiyonel olarak Planning'deki karşılığıyla bağlayabilirsiniz — bu bağlantı veriyi
          değiştirmez, sadece raporlarda iki sistemin aynı fiziksel lokasyonu işaret ettiğini
          gösterir.
        </p>
      </div>

      {/* Yeni depo */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-500" /> Yeni Depo Ekle
          </CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2 flex-wrap items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Slug (sistem adı)</label>
            <Input
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="örn. westerlo"
              className="h-8 text-sm w-40"
              data-testid="depot-list-new-value"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Etiket (görünen ad)</label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Westerlo"
              className="h-8 text-sm w-48"
              data-testid="depot-list-new-label"
            />
          </div>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={creating || !newValue.trim() || !newLabel.trim()}
            data-testid="depot-list-create-btn"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
            Ekle
          </Button>
          <p className="text-[10px] text-slate-400 italic ml-auto">
            Slug bir kez ayarlanır, çalışan profillerinde değişmez. Sadece etiket ve sıralama değiştirilebilir.
          </p>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Link2 className="h-4 w-4 text-blue-500" /> Mevcut Depolar
            <Badge variant="outline" className="ml-1 text-[10px]">{depos.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-xs text-slate-400 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Yükleniyor…
            </div>
          ) : depos.length === 0 ? (
            <div className="text-xs text-slate-400 italic">Henüz depo yok</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="depot-list-table">
                <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left py-2 px-2 w-10">Sıra</th>
                    <th className="text-left py-2 px-2 w-40">Slug</th>
                    <th className="text-left py-2 px-2">Etiket</th>
                    <th className="text-center py-2 px-2 w-24">Çalışan</th>
                    <th className="text-left py-2 px-2 w-72">Planning Karşılığı</th>
                    <th className="text-right py-2 px-2 w-28">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {[...depos].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((depo, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === depos.length - 1;
                    const labelDirty = pendingLabel[depo.id] !== undefined && pendingLabel[depo.id] !== depo.label;
                    const mapDirty = pendingMap[depo.id] !== undefined && pendingMap[depo.id] !== (depo.planning_depot_id || '');
                    const mapVal = pendingMap[depo.id] !== undefined ? pendingMap[depo.id] : (depo.planning_depot_id || '');
                    const labelVal = pendingLabel[depo.id] !== undefined ? pendingLabel[depo.id] : depo.label;
                    const linkedPd = planningOptions.find((o) => o.id === (depo.planning_depot_id || ''));
                    return (
                      <tr key={depo.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`depot-list-row-${depo.id}`}>
                        <td className="py-2 px-2">
                          <div className="flex flex-col gap-0.5">
                            <button
                              type="button"
                              onClick={() => moveOrder(depo, -1)}
                              disabled={isFirst || saving[depo.id]}
                              className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"
                              title="Yukarı taşı"
                            >
                              <ArrowUp className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveOrder(depo, 1)}
                              disabled={isLast || saving[depo.id]}
                              className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"
                              title="Aşağı taşı"
                            >
                              <ArrowDown className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <code className="text-[11px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-300" title="Slug değiştirilemez (çalışan profillerinde referanslıdır)">
                            {depo.value}
                          </code>
                        </td>
                        <td className="py-2 px-2">
                          <Input
                            value={labelVal}
                            onChange={(e) => setPendingLabel((p) => ({ ...p, [depo.id]: e.target.value }))}
                            className={`h-7 text-sm ${labelDirty ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
                            data-testid={`depot-list-label-${depo.id}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          {(() => {
                            const employees = usage[depo.value] || [];
                            const count = employees.length;
                            return (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold transition
                                        ${count === 0
                                          ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
                                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300'
                                        }`}
                                      data-testid={`depot-list-usage-${depo.id}`}
                                    >
                                      <Users className="h-3 w-3" />
                                      {count}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs">
                                    {count === 0 ? (
                                      <p className="text-xs">Bu depoya atanmış çalışan yok</p>
                                    ) : (
                                      <div className="space-y-0.5">
                                        <p className="text-[11px] font-semibold mb-1 opacity-80">{count} çalışan:</p>
                                        <ul className="text-[11px] max-h-60 overflow-y-auto space-y-0.5">
                                          {employees.slice(0, 25).map((e) => (
                                            <li key={e.id}>• {e.full_name}</li>
                                          ))}
                                          {employees.length > 25 && (
                                            <li className="italic opacity-70">+{employees.length - 25} daha…</li>
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-2">
                          <select
                            value={mapVal}
                            onChange={(e) => setPendingMap((p) => ({ ...p, [depo.id]: e.target.value }))}
                            className={`h-7 text-xs w-full rounded border bg-white dark:bg-slate-800 px-2 ${mapDirty ? 'border-amber-400 ring-1 ring-amber-200' : 'border-slate-300 dark:border-slate-700'}`}
                            data-testid={`depot-list-mapping-${depo.id}`}
                          >
                            <option value="">— bağlı değil —</option>
                            {planningOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name} · {o.company}
                              </option>
                            ))}
                          </select>
                          {linkedPd && !mapDirty && (
                            <div className="text-[9px] text-slate-400 mt-0.5">
                              ✓ Planning'de: {linkedPd.name} ({linkedPd.company})
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {(labelDirty || mapDirty) ? (
                            <Button
                              size="sm"
                              variant="default"
                              className="h-7 text-xs px-2"
                              onClick={() => {
                                const patch = {};
                                if (labelDirty) patch.label = labelVal;
                                if (mapDirty) patch.planning_depot_id = mapVal || null;
                                updateDepo(depo, patch);
                              }}
                              disabled={saving[depo.id]}
                              data-testid={`depot-list-save-${depo.id}`}
                            >
                              {saving[depo.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs px-2 text-red-500 hover:bg-red-50 hover:text-red-700"
                              onClick={() => handleDelete(depo)}
                              disabled={deletingId === depo.id}
                              data-testid={`depot-list-delete-${depo.id}`}
                            >
                              {deletingId === depo.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 flex items-start gap-2 text-[10px] text-slate-500 italic">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                <p>
                  Planning Karşılığı bağlantısı sadece raporlarda iki sistemin aynı fiziksel
                  lokasyonu işaret ettiğini gösterir. Çalışanın `depo` alanı (slug) değişmez,
                  Planning'deki Depo & Rota modal'ı bağımsız çalışır.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
